import type { WrappedFieldArrayProps } from 'redux-form';
import React from 'react';
import {
  Map as ImmutableMap,
  Iterable as ImmutableIterable,
  List as ImmutableList,
} from 'immutable';
import FormLabel from '@material-ui/core/FormLabel';
import FormControl from '@material-ui/core/FormControl';
import InputLabel from '@material-ui/core/InputLabel';

import { InputChip } from 'components/InputChip';
import { truthy } from 'utils/truthy';

type Option = { id: string; label: () => string; name: string };

type FormValueSelectorProps<FieldValue = Record<string, unknown>> = {
  options: ImmutableIterable<string | number, Option> | ImmutableList<Option> | Option[];
  labelText: string;
  selectMultiple?: boolean;
  maxAmountAllowed?: number;
  alwaysDisplayErrors?: boolean;
} & WrappedFieldArrayProps<FieldValue>;

const toArray = <T,>(
  values:
    | (T | undefined | null | false)[]
    | ImmutableIterable<string | number, T | undefined | null | false>
): T[] => {
  if (Array.isArray(values)) {
    return values.filter(truthy);
  }
  return (values.filter(truthy) as ImmutableIterable<string | number, T>).toArray();
};

const FormValueSelector = ({
  fields,
  options = ImmutableMap(),
  labelText,
  selectMultiple,
  maxAmountAllowed = 3,
  meta: { error, dirty, submitFailed },
  alwaysDisplayErrors = false,
}: FormValueSelectorProps) => {
  const value = fields.getAll()
    ? fields.getAll().map((field) => field.id || field)
    : ImmutableList();
  const optionIds = options.map((option) => option?.id).filter(Boolean);
  const missingOptions = value.filter((valueId) => !optionIds.includes(valueId));

  const handleDelete = (id: string) => {
    fields.remove(value.indexOf(id));
  };
  return (
    <FormControl style={{ width: '100%' }}>
      <InputLabel shrink>{labelText}</InputLabel>
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          margin: '-4px',
          paddingTop: '30px',
        }}
      >
        {toArray(
          options.map((option) => {
            if (!option) {
              return false;
            }
            const selected = value.includes(option.id);
            const onClick = () => {
              if (
                !selected &&
                (selectMultiple ||
                  (Array.isArray(value) && value.length === 0) ||
                  (value as ImmutableList<unknown>).size === 0)
              ) {
                if (maxAmountAllowed && error && fields.length >= maxAmountAllowed + 1) {
                  fields.pop();
                  return fields.push(option.id);
                }
                return fields.push(option.id);
              }

              return null;
            };

            return (
              <div style={{ margin: '4px' }} key={`${option.id}${option.name}`}>
                <InputChip
                  onClick={onClick}
                  label={option.label ? option.label() : option.name}
                  selected={selected}
                  error={
                    (error && value.indexOf(option.id) === maxAmountAllowed) ||
                    ((dirty || submitFailed) && error && !fields.length)
                  }
                  handleDelete={() => handleDelete(option.id)}
                  variant="outlined"
                />
              </div>
            );
          })
        )}
        {missingOptions.map((optionId) => (
          <InputChip
            key={optionId}
            onClick={() => fields.remove(value.indexOf(optionId))}
            label={optionId}
            selected
            error={
              (error && value.indexOf(optionId) === maxAmountAllowed) ||
              ((dirty || submitFailed) && error && !fields.length)
            }
            handleDelete={() => handleDelete(optionId)}
            variant="outlined"
          />
        ))}
      </div>
      {(dirty || submitFailed || alwaysDisplayErrors) && error && (
        <FormLabel error style={{ fontSize: 12, marginTop: 10, marginLeft: 10 }}>
          {error}
        </FormLabel>
      )}
    </FormControl>
  );
};

export default FormValueSelector;
